import { useState, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { UserRole } from '../../../types/UserRole';
import { UserPermission } from '../../../types/UserPermission';
import { 
  useUserMasterQuery, 
  useUserMasterMutations, 
} from '../../../hooks/useUsersApi';
import { useAppDialog } from '../../../hooks/useAppDialog';

// --- コンポーネントインポート ---
import { UserMaintenanceSearchForm, type UserSearchFilters } from '../../../components/admin/UserMaintenance/UserMaintenanceSearchForm';
import { UserMaintenanceDataTable } from '../../../components/admin/UserMaintenance/UserMaintenanceDataTable';
import { UserMaintenanceEditForm } from '../../../components/admin/UserMaintenance/UserMaintenanceEditForm';
import { PasswordConfirmationModal } from '../../../components/admin/UserMaintenance/PasswordConfirmationModal';

// --- 型定義インポート ---
import type { UserMasterInfo } from '../../../types';
import type { AppUser } from '../../../data/menu';

/**
 * ユーザーメンテナンス ページコンポーネント
 * * システム利用者の情報を一元管理（検索・一覧表示・新規登録・編集・削除・パスワード初期化）します。
 * 役割（Admin/Seller/Farmer/Buyer）ごとにタブを切り替えて表示します。
 */
const UserMaintenancePage = () => {
  // --- コンテキスト & API Hooks ---
  const { currentUser } = useAuth();
  const { data: users = [], isLoading } = useUserMasterQuery(!!currentUser);
  const { 
    createMutation, 
    updateMutation, 
    deleteMutation, 
    resetPasswordMutation 
  } = useUserMasterMutations();

  // 共通ダイアログ用フック
  const { showConfirm, OverlayElement } = useAppDialog();

  const pageTitle = "ユーザーメンテ機能";

  // --- 型変換ロジック ---
  /**
   * ログイン中のユーザー情報を DataTable コンポーネントが期待する型 (AppUser) に変換します。
   * 自分自身を操作不能にする判定に使用されます。
   */
  const convertedUser: AppUser | null = useMemo(() => {
    if (!currentUser) return null;
    const permissionValue = currentUser.permission !== undefined ? Number(currentUser.permission) : 0;
    return {
      Id: currentUser.loginId || (currentUser as any).id || (currentUser as any).Id || "unknown",
      UserName: currentUser.name || (currentUser as any).username || (currentUser as any).UserName || "ユーザー",
      role: currentUser.role, 
      permission: permissionValue as UserPermission // ここで数値として渡す
    };
  }, [currentUser]);

  // 編集者（UserPermission.Editor = 1）かどうかを判定
  const isAdmin = convertedUser?.permission === UserPermission.Editor || convertedUser?.permission === UserPermission.Admin || convertedUser?.permission === UserPermission.Creator;

  // --- ローカルState管理 ---
  
  /** 選択中の役割タブ（初期値：市場） */
  const [selectedRole, setSelectedRole] = useState<UserRole>(UserRole.Admin);
  
  /** 検索フィルタ条件 */
  const [filters, setFilters] = useState<UserSearchFilters>({ 
    code: '', 
    name: '', 
    permission: undefined,
    includeDeleted: false,
    onlyLocked: false,
  });
  
  /** 編集・新規登録モーダルの表示制御 */
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  /** 編集対象のユーザー情報（nullの場合は新規登録モード） */
  const [editingUser, setEditingUser] = useState<UserMasterInfo | null>(null);

  /** 初期化・発行されたパスワード情報の通知用State */
  const [issuedPasswordData, setIssuedPasswordData] = useState<{
    userName: string;
    loginId: string;
    newPassword: string;
  } | null>(null);

  // --- 描画・定数定義 ---
  
  /** 表示用タブの定義リスト */
  const tabs = [
    { label: '市場', role: UserRole.Admin },
    { label: 'JA', role: UserRole.JA },
    { label: '売手', role: UserRole.Seller },
    { label: '生産者', role: UserRole.Farmer },
    { label: '買手', role: UserRole.Buyer },
  ];

  // --- フィルタリングロジック ---

  /**
   * 取得済みデータに対して、役割・削除フラグ・検索フィルターを適用した結果。
   * サーバーサイドではなくクライアントサイドで絞り込みを行います。
   */
  const filteredData = useMemo(() => {
    return users.filter(u => {
      // 1. 役割(Tab)での一致
      const matchRole = u.role === selectedRole;
      // 2. 削除済みを含めるか（チェック無しなら有効データのみ表示）
      const matchDeleted = filters.includeDeleted ? true : !u.isDeleted;
      // 3. ログインID(前方一致)
      const matchCode = u.loginId.startsWith(filters.code || '');
      // 4. 氏名(部分一致)
      const matchName = u.username.includes(filters.name || '');
      // 5. 権限での一致
      const matchPermission = filters.permission === undefined || u.permission === filters.permission;
      // 6. ロック状態のみ
      const matchLocked = !filters.onlyLocked || u.isLocked;

      return matchRole && matchDeleted && matchCode && matchName && matchPermission && matchLocked;
    });
  }, [users, selectedRole, filters]);

  // --- イベントハンドラー ---

  /**
   * 新規登録ボタンクリック時のハンドラー
   * 選択中の役割をデフォルト値として初期化した空のユーザーオブジェクトを作成します。
   */
  const handleCreateClick = () => {
    if (!isAdmin) return;

    setEditingUser({
      id: '',
      loginId: '',
      username: '',
      role: selectedRole,
      masterCode: '',
      maxSessionCount: 1,
      permission: UserPermission.Editor, 
      isDeleted: false,
      isLocked: false,
      lastLoginAt: null
    });
    setIsEditModalOpen(true);
  };

  /**
   * 保存処理ハンドラー
   * 新規(POST)または更新(PUT)をIDの有無で判断し、APIリクエストを実行します。
   * * @param data - フォームから渡されたユーザーデータ
   */
  const handleSave = (data: any) => {
    const payload = {
      id: editingUser?.id || "", 
      loginId: data.loginId,
      userName: data.username || data.userName,
      role: data.role,
      masterCode: data.masterCode,
      maxSessionCount: Number(data.maxSessionCount),
      permission: data.permission,
      isDeleted: data.isDeleted,
    };

    if (editingUser?.id) {
      // 既存ユーザーの更新
      updateMutation.mutate(
        { id: editingUser.id, req: payload }, 
        { onSuccess: () => setIsEditModalOpen(false) }
      );
    } else {
      // 新規ユーザーの登録
      createMutation.mutate(payload, {
        onSuccess: (res: any) => {
          setIsEditModalOpen(false);
          const password = res.initPassword || res.password || res.newPassword;
          if (password) {
            setIssuedPasswordData({
              userName: res.username || res.userName || data.username,
              loginId: res.loginId || data.loginId,
              newPassword: password
            });
          }
        }
      });
    }
  };

  /**
   * パスワード初期化実行ハンドラー
   * 実行後、サーバーから返却された新パスワードを表示用モーダルにセットします。
   * * @param u - 対象ユーザー情報
   */
  const handleResetPassword = (u: UserMasterInfo) => {
    showConfirm({
      title: pageTitle,
      message: `${u.username} のパスワードを初期化しますか？`,
      onConfirm: () => {
        resetPasswordMutation.mutate(u.id, {
          onSuccess: (res: any) => {
            const password = res.initPassword || res.password || res.newPassword;
            if (password) {
              setIssuedPasswordData({
                userName: u.username,
                loginId: u.loginId,
                newPassword: password
              });
            }
          }
        });
      }
    });
  };

  // --- メインレンダリング ---
  return (
    <div className="p-4 md:p-6 space-y-4 bg-gray-50 min-h-screen flex flex-col relative">
      
      {/* 共通背景レイヤー */}
      {OverlayElement}

      {/* ページヘッダー：タイトルと新規登録ボタン */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-2 flex-shrink-0">
        <h1 className="text-xl font-bold text-gray-800">{pageTitle}</h1>
        <button 
          onClick={handleCreateClick}
          disabled={!isAdmin}
          className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded shadow text-sm font-bold transition-all w-full sm:w-auto ${
          isAdmin 
            ? "bg-indigo-600 hover:bg-indigo-700 text-white" 
            : "bg-gray-300 text-gray-500 cursor-not-allowed opacity-60"
        }`}
      >
        <Plus size={18} /> 新規ユーザー登録
      </button>
      </div>

      {/* 役割選択タブエリア：市場/JA/売手/生産者/買手 */}
      <div className="flex border-b border-gray-200 bg-white px-2 md:px-4 rounded-t-lg shadow-sm overflow-x-auto no-scrollbar">
        {tabs.map(tab => (
          <button
            key={tab.role}
            onClick={() => setSelectedRole(tab.role)}
            className={`whitespace-nowrap px-4 md:px-6 py-3 text-base font-bold transition-all relative ${
              selectedRole === tab.role ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {tab.label}
            {selectedRole === tab.role && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-600 rounded-t" />
            )}
          </button>
        ))}
      </div>

      {/* 検索条件指定フォーム */}
      <UserMaintenanceSearchForm 
        onSearch={setFilters} 
        initialFilters={filters} 
      />

      {/* ユーザー一覧データテーブル */}
      <div className="flex-1">
        <UserMaintenanceDataTable 
          data={filteredData} 
          isLoading={isLoading}
          currentUser={convertedUser}
          currentRole={selectedRole}
          onEdit={(u) => { setEditingUser(u); setIsEditModalOpen(true); }}
          onResetPassword={handleResetPassword}
          onDelete={(u) => {
            showConfirm({
              title: pageTitle,
              message: `${u.username} を無効化しますか？`,
              onConfirm: () => {
                deleteMutation.mutate(u.id);
              }
            });
          }}
        />
      </div>

      {/* 共通モーダル：ユーザー情報の編集・登録 */}
      {isEditModalOpen && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-4xl shadow-2xl overflow-y-auto max-h-[95vh]">
            <UserMaintenanceEditForm 
              originalUser={editingUser}
              currentUser={currentUser}
              isCreating={!editingUser.id}
              onSave={handleSave}
              onCancel={() => setIsEditModalOpen(false)}
              isSaving={createMutation.isPending || updateMutation.isPending}
            />
          </div>
        </div>
      )}

      {/* 共通モーダル：初期化・発行パスワードの確認画面 */}
      {issuedPasswordData && (
        <PasswordConfirmationModal
          userName={issuedPasswordData.userName}
          loginId={issuedPasswordData.loginId}
          newPassword={issuedPasswordData.newPassword}
          onClose={() => setIssuedPasswordData(null)}
        />
      )}
    </div>
  );
};

export default UserMaintenancePage;